import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AmountInput } from '@/components/AmountInput'
import { CurrencyAmountInput } from '@/components/CurrencyAmountInput'
import { FieldLabel } from '@/components/FieldLabel'
import { ElectionsApi } from '@/services/political/electionsApi'
import { cn } from '@/lib/utils'
import { formatNumber } from '@/utils/format'
import {
  budgetIssues,
  newBudgetDraft,
  newTopicDraft,
  type PoliticalBudgetDraft,
  type TopicBudgetDraft,
} from '@/utils/advertiserPolitical'
import { ElectionSelect, ELECTIONS_QUERY_KEY } from './ElectionSelect'

export interface AdvertiserPoliticalBudgetsEditorProps {
  value: PoliticalBudgetDraft[]
  onChange: (value: PoliticalBudgetDraft[]) => void
  /** Currency for a newly added election budget. */
  defaultCurrency: string
  /** Show what stops each budget from saving — once a save has been tried. */
  showIssues?: boolean
}

/** How much of the total the topics take up, once both are known. */
function topicAllocation(
  budget: PoliticalBudgetDraft,
  defaultCurrency: string,
  t: (key: string, options?: Record<string, unknown>) => string
): { text: string; over: boolean } | null {
  const total = Number(budget.totalBudget)
  if (!budget.totalBudget || !Number.isFinite(total) || budget.topics.length === 0) return null
  const allocated = budget.topics.reduce((sum, topic) => sum + (Number(topic.amount) || 0), 0)
  const money = (amount: number) =>
    formatNumber(amount, { style: 'currency', currency: budget.currency || defaultCurrency })
  return allocated > total
    ? { text: t('political.budgetExceeded', { amount: money(allocated - total) }), over: true }
    : { text: t('advertisers.topicsAllocated', { allocated: money(allocated), total: money(total) }), over: false }
}

function FieldError({ children }: { children?: string }) {
  return children ? <p className="text-xs text-destructive">{children}</p> : null
}

/**
 * Edits the budgets an advertiser declares per election, and the topics within
 * each that its political line items can book against.
 */
export function AdvertiserPoliticalBudgetsEditor({
  value,
  onChange,
  defaultCurrency,
  showIssues,
}: AdvertiserPoliticalBudgetsEditorProps) {
  const { t } = useTranslation()
  const { data: elections } = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
  })
  const issues = showIssues ? budgetIssues(value) : {}

  const updateBudget = (key: string, patch: Partial<PoliticalBudgetDraft>) =>
    onChange(value.map((b) => (b.key === key ? { ...b, ...patch } : b)))

  const updateTopic = (budget: PoliticalBudgetDraft, topicKey: string, patch: Partial<TopicBudgetDraft>) =>
    updateBudget(budget.key, {
      topics: budget.topics.map((topic) => (topic.key === topicKey ? { ...topic, ...patch } : topic)),
    })

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('advertisers.noPoliticalBudgets')}</p>
      )}

      {value.map((budget) => {
        const budgetIssue = issues[budget.key]
        // Each election takes one budget, so the others' elections are not offered.
        const electionIds = elections
          ?.map((e) => e.id)
          .filter((id) => !value.some((b) => b.key !== budget.key && b.electionId === id))
        const allocation = topicAllocation(budget, defaultCurrency, t)
        return (
          <div key={budget.key} className="overflow-hidden rounded-lg border">
            <div className="p-3">
              <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto]">
                <div className="p-field">
                  <FieldLabel>{t('political.election')}</FieldLabel>
                  <ElectionSelect
                    value={budget.electionId}
                    onChange={(electionId) => updateBudget(budget.key, { electionId })}
                    electionIds={electionIds}
                    invalid={!!budgetIssue?.election}
                  />
                  <FieldError>
                    {budgetIssue?.election === 'duplicate'
                      ? t('advertisers.budgetElectionDuplicate')
                      : budgetIssue?.election && t('advertisers.budgetElectionRequired')}
                  </FieldError>
                </div>
                <div className="p-field">
                  <FieldLabel>{t('political.totalBudget')}</FieldLabel>
                  <CurrencyAmountInput
                    value={budget.totalBudget}
                    onChange={(totalBudget) => updateBudget(budget.key, { totalBudget })}
                    currency={budget.currency}
                    onCurrencyChange={(currency) => updateBudget(budget.key, { currency })}
                    currencyFallback={defaultCurrency}
                  />
                  <FieldError>{budgetIssue?.totalBudget && t('advertisers.budgetTotalRequired')}</FieldError>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="sm:mt-6"
                  onClick={() => onChange(value.filter((b) => b.key !== budget.key))}
                  title={t('advertisers.removeElectionBudget')}
                  aria-label={t('advertisers.removeElectionBudget')}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </div>

            {/* The topics split the election's budget, so they sit in the same
                block and columns: each amount lines up under the total. */}
            <div className="flex flex-col gap-2 border-t bg-muted/40 p-3">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium">
                  {t('political.topics')}
                  {budget.topics.length > 0 && (
                    <span className="ml-1.5 font-normal text-muted-foreground">{budget.topics.length}</span>
                  )}
                </span>
                {allocation && (
                  <span className={cn('text-xs', allocation.over ? 'text-destructive' : 'text-muted-foreground')}>
                    {allocation.text}
                  </span>
                )}
              </div>
              {budget.topics.length === 0 && (
                <p className="text-sm text-muted-foreground">{t('advertisers.noTopicsYet')}</p>
              )}
              {budget.topics.map((topic) => {
                const topicIssue = budgetIssue?.topics[topic.key]
                return (
                  <div
                    key={topic.key}
                    className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_auto]"
                  >
                    <div className="flex flex-col gap-1">
                      <Input
                        value={topic.name}
                        onChange={(e) => updateTopic(budget, topic.key, { name: e.target.value })}
                        placeholder={t('political.topicNamePlaceholder')}
                        aria-label={t('political.topicName')}
                        aria-invalid={!!topicIssue?.name || undefined}
                      />
                      <FieldError>
                        {topicIssue?.name === 'duplicate'
                          ? t('advertisers.topicNameDuplicate')
                          : topicIssue?.name && t('advertisers.topicNameRequired')}
                      </FieldError>
                    </div>
                    <div className="flex flex-col gap-1">
                      <AmountInput
                        value={topic.amount}
                        onChange={(amount) => updateTopic(budget, topic.key, { amount })}
                        unit={budget.currency || defaultCurrency}
                      />
                      <FieldError>{topicIssue?.amount && t('advertisers.topicAmountRequired')}</FieldError>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        updateBudget(budget.key, { topics: budget.topics.filter((x) => x.key !== topic.key) })
                      }
                      title={t('political.removeTopic')}
                      aria-label={t('political.removeTopic')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )
              })}
              <div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="-ml-2"
                  onClick={() => updateBudget(budget.key, { topics: [...budget.topics, newTopicDraft()] })}
                >
                  <Plus className="size-4" />
                  {t('political.addTopic')}
                </Button>
              </div>
            </div>
          </div>
        )
      })}

      <div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...value, newBudgetDraft(defaultCurrency)])}
        >
          <Plus className="size-4" />
          {t('advertisers.addElectionBudget')}
        </Button>
      </div>
    </div>
  )
}
